import { useEffect } from 'react';
import { X, AlertTriangle, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { logAnalyticsEvent } from '@/lib/firebase';
import { isLaunchWeekActive, LAUNCH_REGULAR_PRICES } from './LaunchCountdown';
import { PLAN_CONFIGS, isOneTimePlan, type PlanId } from '@shared/types';

interface QuotaLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'leads' | 'sites';
  currentUsage: number;
  limit: number;
  planId?: PlanId;
}

// Where each plan sends a user who has run out of quota.
//
// Lifetime jumps straight to Cashflow rather than Starter: someone who has
// burned 1,000 leads has demonstrably outgrown Starter's 75/month, and dropping
// them onto a smaller monthly allowance than the one they just exhausted would
// read as a downgrade they have to pay for.
const UPGRADE_PATH: Record<PlanId, PlanId | null> = {
  free: 'starter',
  lifetime: 'cashflow',
  // Same product as lifetime, same exhausted allowance, same next step.
  lifetime_offer: 'cashflow',
  // The mobile $49 plan steps onto the desktop ladder at Starter: double the
  // leads for double the price, the smallest coherent jump from 75/month.
  monthly: 'starter',
  starter: 'cashflow',
  cashflow: 'scale',
  // Scale is now the top of the ladder — Agency is retired, so there is nothing
  // above Scale to upsell to.
  scale: null,
  agency: null,
};

export function QuotaLimitModal({
  isOpen,
  onClose,
  type,
  currentUsage,
  limit,
  planId = 'starter',
}: QuotaLimitModalProps) {
  const navigate = useNavigate();

  const typeLabels = {
    leads: { singular: 'lead', plural: 'leads', action: 'scrape more leads' },
    sites: { singular: 'site', plural: 'sites', action: 'generate more sites' },
  };

  const label = typeLabels[type];
  const nextPlanId = UPGRADE_PATH[planId];
  const canUpgrade = nextPlanId !== null;
  const currentPlanName = PLAN_CONFIGS[planId].name;
  // A one-time plan's allowance never refills, so the "your quota resets next
  // month" reassurance below would be a straight lie to a lifetime user — and
  // the one thing that reliably turns a paywall into a chargeback.
  const isOneTime = isOneTimePlan(planId);
  // LAUNCH_REGULAR_PRICES only has anchors for the four subscription tiers.
  // Narrowing here keeps the strikethrough below from having to know that.
  const launchAnchorPlan =
    nextPlanId &&
    nextPlanId !== 'free' &&
    nextPlanId !== 'lifetime' &&
    nextPlanId !== 'lifetime_offer' &&
    nextPlanId !== 'monthly'
      ? nextPlanId
      : null;
  const showLaunchAnchor = isLaunchWeekActive() && launchAnchorPlan !== null;
  const nextPlan = nextPlanId
    ? {
        name: PLAN_CONFIGS[nextPlanId].name,
        price: PLAN_CONFIGS[nextPlanId].price,
        leads: PLAN_CONFIGS[nextPlanId].quotas.leadsLimit,
        sites: PLAN_CONFIGS[nextPlanId].quotas.sitesLimit,
      }
    : null;

  useEffect(() => {
    if (isOpen) {
      logAnalyticsEvent('quota_wall_hit', {
        type,
        plan_id: planId,
        usage: currentUsage,
        limit,
      });
    }
  }, [isOpen, type, planId, currentUsage, limit]);

  if (!isOpen) return null;

  const handleUpgrade = () => {
    logAnalyticsEvent('quota_wall_upgrade_clicked', {
      type,
      plan_id: planId,
      next_plan_id: nextPlanId,
    });
    onClose();
    navigate('/dashboard/settings?tab=billing');
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-modal-backdrop"
      style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden animate-modal-in"
        style={{
          background: 'linear-gradient(to bottom, #000000, #000000)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Header */}
        <div className="relative p-6 pb-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 text-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(91, 58, 58, 0.2), rgba(91, 58, 58, 0.2))',
                border: '1px solid rgba(91, 58, 58, 0.3)',
              }}
            >
              <AlertTriangle className="w-7 h-7 text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white font-display">
                Quota Reached
              </h2>
              <p className="text-gray-500 text-sm">
                You've used all your {label.plural}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Usage display */}
          <div
            className="rounded-xl p-4 mb-6"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400 text-sm capitalize">{type} Used</span>
              <span className="text-white font-medium">
                {currentUsage} / {limit}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-400"
                style={{ width: '100%' }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {isOneTime
                ? `Your ${currentPlanName} plan includes ${limit.toLocaleString()} ${label.plural}, one time`
                : `Your ${currentPlanName} plan includes ${limit.toLocaleString()} ${label.plural} per month`}
            </p>
          </div>

          {/* Message */}
          <p className="text-gray-400 text-sm mb-6">
            {isOneTime && canUpgrade && nextPlan ? (
              // A lifetime user hitting the cap needs to hear BOTH things: the
              // allowance was one-time (not a monthly limit that will reset), and
              // the way forward is a monthly plan. The old copy only ever showed
              // the generic "upgrade for X/month" line, so the "doesn't renew"
              // message this modal was written to give a lifetime user never
              // actually rendered.
              <>
                You've used your full {currentPlanName} allowance, and it doesn't renew. Move to {nextPlan.name}
                {' '}for <span className="text-primary-400 font-medium">{nextPlan[type].toLocaleString()} {label.plural}</span> a month to keep going.
              </>
            ) : canUpgrade && nextPlan ? (
              <>
                Upgrade to {nextPlan.name} for <span className="text-primary-400 font-medium">{nextPlan[type].toLocaleString()} {label.plural}</span> per month
                and keep {label.action === 'scrape more leads' ? 'scraping' : 'generating'}.
              </>
            ) : isOneTime ? (
              <>
                You've used your full {currentPlanName} allowance. It doesn't renew. Contact support
                for additional capacity.
              </>
            ) : (
              <>
                You've reached your monthly limit. Your quota will reset at the beginning of next month,
                or contact support for additional capacity.
              </>
            )}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            {canUpgrade && nextPlan ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onClose}
                >
                  Maybe Later
                </Button>
                <div className="flex-1 relative">
                  {showLaunchAnchor && (
                    <span
                      className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-primary-500 text-[9px] font-bold tracking-wider uppercase text-white whitespace-nowrap z-10"
                      style={{ boxShadow: '0 0 14px rgba(176,128,128,0.55)' }}
                    >
                      Launch Offer
                    </span>
                  )}
                  <Button
                    className="w-full gap-2"
                    onClick={handleUpgrade}
                    style={{
                      background: 'linear-gradient(135deg, #B08080, #B08080)',
                    }}
                  >
                    <Zap className="w-4 h-4" />
                    {showLaunchAnchor && launchAnchorPlan ? (
                      <span className="flex items-center gap-1.5">
                        Upgrade
                        <span className="line-through text-white/50 text-xs">
                          ${LAUNCH_REGULAR_PRICES[launchAnchorPlan]}
                        </span>
                        <span className="font-bold">${nextPlan.price}/mo</span>
                      </span>
                    ) : (
                      <span>Upgrade · ${nextPlan.price}/mo</span>
                    )}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={onClose}
              >
                Got it
              </Button>
            )}
          </div>

          {/* Next plan benefits preview */}
          {canUpgrade && nextPlan && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-gray-500 mb-3">{nextPlan.name} plan includes:</p>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2 rounded-lg bg-white/5">
                  <p className="text-lg font-semibold text-white font-display">
                    {nextPlan.leads.toLocaleString()}
                    <span className="text-xs text-gray-500 font-normal font-sans">/mo</span>
                  </p>
                  <p className="text-xs text-gray-500">Leads</p>
                </div>
                <div className="p-2 rounded-lg bg-white/5">
                  <p className="text-lg font-semibold text-white font-display">
                    {nextPlan.sites.toLocaleString()}
                    <span className="text-xs text-gray-500 font-normal font-sans">/mo</span>
                  </p>
                  <p className="text-xs text-gray-500">Sites</p>
                </div>
              </div>
              <p className="text-[10px] text-gray-600 mt-2 text-center">Renews monthly</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default QuotaLimitModal;
